import round from "lodash/round";
import { Length, Size, Unit } from "../../types/customization/box";
import {
  TemplateEditablePictureDefinition,
  TemplatePageDefinition,
} from "../../types/customization/template";
import {
  calcAbsolutePosition,
  lengthMmToPx,
  positionDmmToMm,
  sizeDmmToMm,
  sizeMmToPx,
} from "../../utils/customization/box";

export function calcBleedingEdges(
  elementDefinition: TemplateEditablePictureDefinition,
  pageDefinition: TemplatePageDefinition
): {
  bottom: Length<Unit.MM>;
  left: Length<Unit.MM>;
  top: Length<Unit.MM>;
  right: Length<Unit.MM>;
} {
  const elementSizeMm = sizeDmmToMm(elementDefinition);
  const pageSize: Size<Unit.MM> = pageDefinition;
  const absolutePosition = calcAbsolutePosition<Unit.MM>({
    elementAnchor: elementDefinition.anchorSelf,
    elementPosition: positionDmmToMm(elementDefinition),
    elementSize: elementSizeMm,
    pageAnchor: elementDefinition.anchorPage,
    pageSize,
  });

  const left = { ...absolutePosition.x };
  left.value = Math.abs(Math.min(0, absolutePosition.x.value));

  const top = { ...absolutePosition.y };
  top.value = Math.abs(Math.min(0, absolutePosition.y.value));

  const bottom = { ...elementSizeMm.height };
  bottom.value = Math.abs(
    Math.min(0, pageSize.height.value - absolutePosition.y.value - elementSizeMm.height.value)
  );

  const right = { ...elementSizeMm.height };
  right.value = Math.abs(
    Math.min(0, pageSize.width.value - absolutePosition.x.value - elementSizeMm.width.value)
  );

  return { bottom, left, top, right };
}

export function svgBleedingMask(
  elementDefinition: TemplateEditablePictureDefinition,
  pageDefinition: TemplatePageDefinition
): string {
  const maskPosition = calcBleedingEdges(elementDefinition, pageDefinition);
  const elementSizeMm = sizeDmmToMm(elementDefinition);

  const widthMm = elementSizeMm.width.value;
  const heightMm = elementSizeMm.height.value;

  const xStart = maskPosition.left.value;
  const yStart = maskPosition.top.value;

  const rightStart = maskPosition.right.value;
  const bottomStart = maskPosition.bottom.value;

  const visibleWidth = widthMm - xStart - rightStart;
  const visibleHeight = heightMm - yStart - bottomStart;

  return `
    <svg viewBox="0 0 ${round(widthMm, 1)} ${round(heightMm, 1)}">
      <rect fill="#fff" x="${Math.max(xStart, 0)}" y="${Math.max(yStart, 0)}" width="${round(
    visibleWidth,
    1
  )}" height="${round(visibleHeight, 1)}" />
    </svg>
  `;
}

export function hasBleedingEdge(
  elementDefinition: TemplateEditablePictureDefinition,
  pageDefinition: TemplatePageDefinition
): { x: boolean; y: boolean } {
  const bleedingEdges = calcBleedingEdges(elementDefinition, pageDefinition);
  const xBleed = bleedingEdges.left.value > 0 || bleedingEdges.right.value > 0;
  const yBleed = bleedingEdges.top.value > 0 || bleedingEdges.bottom.value > 0;

  return { x: xBleed, y: yBleed };
}
export function calcDropZone(
  elementDefinition: TemplateEditablePictureDefinition,
  pageDefinition: TemplatePageDefinition,
  ppmm: number
): {
  height: Length<Unit.PX>;
  left: Length<Unit.PX>;
  top: Length<Unit.PX>;
  width: Length<Unit.PX>;
} {
  const elementSizeMm = sizeDmmToMm(elementDefinition);
  const elementSizePx = sizeMmToPx(elementSizeMm, ppmm);

  const hasBleedingEdges = hasBleedingEdge(elementDefinition, pageDefinition);
  const bleedingEdges = calcBleedingEdges(elementDefinition, pageDefinition);
  const bleedingEdgesPx = {
    bottom: lengthMmToPx(bleedingEdges.bottom, ppmm),
    left: lengthMmToPx(bleedingEdges.left, ppmm),
    right: lengthMmToPx(bleedingEdges.right, ppmm),
    top: lengthMmToPx(bleedingEdges.top, ppmm),
  };
  const dropZoneHeight = hasBleedingEdges.y
    ? elementSizePx.height.value - bleedingEdgesPx.bottom.value - bleedingEdgesPx.top.value
    : elementSizePx.height.value;
  const dropZoneWidth = hasBleedingEdges.x
    ? elementSizePx.width.value - bleedingEdgesPx.left.value - bleedingEdgesPx.right.value
    : elementSizePx.width.value;

  return {
    height: { unit: Unit.PX, value: dropZoneHeight },
    left: { unit: Unit.PX, value: bleedingEdgesPx.left.value },
    top: { unit: Unit.PX, value: bleedingEdgesPx.top.value },
    width: { unit: Unit.PX, value: dropZoneWidth },
  };
}
